/**
 * Adaptive message body — renders a message at the cadence chosen by the
 * Adaptive Cinematic Reveal Engine, with per-style typography and layout
 * alignment. Used inside every scene to replace inline `words.map` blocks.
 */

import { motion } from 'framer-motion';
import { useReadingRhythm, type RhythmMode } from './useReadingRhythm';

const EASE = [0.22, 0.61, 0.36, 1] as const;

export type CompositionAlignment = 'centered' | 'asymmetric-left' | 'asymmetric-right' | 'editorial-grid' | 'cinematic-stack' | 'framed';

export type MessageTypography =
  | 'serif-classic'
  | 'serif-editorial'
  | 'sans-modern'
  | 'serif-handwritten'
  | 'mixed-magazine';

export interface AdaptiveMessageBodyProps {
  message: string;
  mode: RhythmMode;
  pacing: number;
  visible: boolean;
  /** Alignment / layout variant. Falls back to centered. */
  alignment?: CompositionAlignment;
  /** Initial delay before the first chunk (s). */
  startDelay?: number;
  /** Tailwind text color class (e.g. 'text-amber-950'). */
  textColorClass?: string;
  /** CSS color string for emphasis glow. */
  emphasisGlow?: string;
  /** Tailwind font class — overrides typography mapping when provided. */
  fontClass?: string;
  /** Tailwind size class — overrides default size. */
  sizeClass?: string;
  /** Typography preset (from relationship profile or emotional theme). */
  typography?: MessageTypography;
}

const ALIGN_TO_WRAPPER: Record<CompositionAlignment, string> = {
  centered: 'text-center mx-auto max-w-xl',
  'asymmetric-left': 'text-left mr-auto max-w-xl pl-2 sm:pl-0 sm:pr-12',
  'asymmetric-right': 'text-right ml-auto max-w-xl pr-2 sm:pr-0 sm:pl-12',
  'editorial-grid': 'text-left mx-auto max-w-2xl sm:columns-1 md:columns-2 md:gap-10',
  'cinematic-stack': 'text-left mx-auto max-w-2xl space-y-6',
  framed: 'text-center mx-auto max-w-lg px-6 sm:px-10 py-6 border border-current/10 rounded-2xl backdrop-blur-[2px]',
};

const TYPOGRAPHY_FONT: Record<MessageTypography, string> = {
  'serif-classic': 'font-serif tracking-normal',
  'serif-editorial': 'font-serif tracking-[0.005em]',
  'sans-modern': 'font-sans tracking-tight',
  'serif-handwritten': 'font-serif italic tracking-wide',
  'mixed-magazine': 'font-serif tracking-tight',
};

const FONT_BY_RHYTHM: Record<RhythmMode, string> = {
  word: 'font-serif',
  sentence: 'font-serif',
  paragraph: 'font-sans',
};

const SIZE_BY_RHYTHM: Record<RhythmMode, string> = {
  word: 'text-xl md:text-2xl leading-relaxed',
  sentence: 'text-xl md:text-[1.6rem] leading-[1.55]',
  paragraph: 'text-lg md:text-xl leading-[1.7] tracking-wide',
};

export function AdaptiveMessageBody({
  message,
  mode,
  pacing,
  visible,
  alignment = 'centered',
  startDelay = 0,
  textColorClass = 'text-foreground',
  emphasisGlow,
  fontClass,
  sizeClass,
  typography,
}: AdaptiveMessageBodyProps) {
  const rhythm = useReadingRhythm({ message, mode, pacing, startDelay });

  const wrapper = ALIGN_TO_WRAPPER[alignment] ?? ALIGN_TO_WRAPPER.centered;
  const font = fontClass ?? (typography ? TYPOGRAPHY_FONT[typography] : FONT_BY_RHYTHM[mode]);
  const size = sizeClass ?? SIZE_BY_RHYTHM[mode];

  if (mode === 'word') {
    return (
      <p className={`${wrapper} ${font} ${size} ${textColorClass} whitespace-pre-wrap`}>
        {rhythm.chunks.map((c, i) => (
          <motion.span
            key={i}
            initial={{ opacity: 0, y: 8, filter: c.emphasis ? 'blur(6px)' : 'blur(0px)' }}
            animate={visible ? {
              opacity: 1,
              y: 0,
              filter: 'blur(0px)',
              textShadow: c.emphasis && emphasisGlow ? `0 0 12px ${emphasisGlow}` : 'none',
            } : {}}
            transition={{ delay: c.delay, duration: c.duration, ease: EASE as any }}
            className="inline-block"
          >
            {c.text}{' '}
          </motion.span>
        ))}
      </p>
    );
  }

  if (mode === 'sentence') {
    return (
      <div className={`${wrapper} ${textColorClass} space-y-4`}>
        {rhythm.chunks.map((c, i) => (
          <motion.p
            key={i}
            initial={{ opacity: 0, y: 14, filter: 'blur(8px)' }}
            animate={visible ? {
              opacity: 1, y: 0, filter: 'blur(0px)',
              textShadow: c.emphasis && emphasisGlow ? `0 0 18px ${emphasisGlow}` : 'none',
            } : {}}
            transition={{ delay: c.delay, duration: c.duration, ease: EASE as any }}
            className={`${font} ${size} ${c.emphasis ? 'opacity-100' : 'opacity-90'}`}
          >
            {c.text}
          </motion.p>
        ))}
      </div>
    );
  }

  // paragraph
  return (
    <div className={`${wrapper} ${textColorClass} space-y-7`}>
      {rhythm.chunks.map((c, i) => (
        <motion.p
          key={i}
          initial={{ opacity: 0, y: 18, filter: 'blur(10px)' }}
          animate={visible ? {
            opacity: 1, y: 0, filter: 'blur(0px)',
            textShadow: c.emphasis && emphasisGlow ? `0 0 22px ${emphasisGlow}` : 'none',
          } : {}}
          transition={{ delay: c.delay, duration: c.duration, ease: EASE as any }}
          className={`${font} ${size} whitespace-pre-wrap`}
        >
          {c.text}
        </motion.p>
      ))}
    </div>
  );
}
